/* ---- SCR-001: Map page (current location) ----
 * ジオフェンス取得・位置情報送信は SDK 内で完結。
 * WebView は SDK からのコールバックで UI を更新するのみ。
 */

import { sdkBridge } from '../bridge/sdk-bridge';
import { renderLocationInfoPanel } from '../components/LocationInfoPanel';
import { MapViewComponent } from '../components/MapView';
import { renderSpoofingAlert } from '../components/SpoofingAlert';
import { ICONS } from '../icons';
import { eventService } from '../services/event.service';
import type { Geofence, LocationData, SpoofingStatus } from '../types';

/**
 * Ray Casting 判定 (point-in-polygon)。
 * iOS/browser で SDK の onLocationContext が発火しないため WebView 側で簡易 in/out 判定する。
 * Android では本関数は呼ばない (SDK が確率計算を実施するため)。
 *
 * polygon: [[lng, lat], ...] 形式 (Geofence.coordinates と同じ順序)。
 */
function pointInPolygon(lng: number, lat: number, polygon: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0];
    const yi = polygon[i][1];
    const xj = polygon[j][0];
    const yj = polygon[j][1];
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/** 2 点間距離 (メートル) — reverse-geocode throttle 用の簡易 Haversine */
function distanceMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export class MapPage {
  private el: HTMLElement;
  private mapView: MapViewComponent | null = null;
  private currentLocation: LocationData | null = null;
  private spoofingStatus: SpoofingStatus | null = null;
  private alertVisible = true;
  private address = '位置情報を取得中...';
  private geofences: Geofence[] = [];
  private currentGeofenceNames: string[] = [];
  private addressConfidence = 0;
  private geofenceCandidates: { name: string; probability: number; inSchedule: boolean }[] = [];
  // Phase 2-b: 環境推定 (INDOOR / OUTDOOR / UNKNOWN) と信頼度 (HIGH / MEDIUM / LOW)
  private environment = 'UNKNOWN';
  private environmentAccuracy = 'LOW';
  /** Phase 4: 位置欺瞞検知の詳細リスト (アコーディオン) 開閉状態。spoofing_status 更新時の
   *  panel 再描画でもユーザの操作状態を維持するためクラス側で保持する。 */
  private spoofingDetailExpanded = false;

  /** DOM 構築済フラグ。タブ切替で render() が再呼び出しされても DOM を再構築しない (= SDK monitoring と OpenLayers Map を継続)。 */
  private built = false;

  /** iOS/browser 用 reverse-geocode throttle: 直前呼び出し座標と時刻 (Android では未使用) */
  private lastGeocodeLat: number | null = null;
  private lastGeocodeLng: number | null = null;
  private lastGeocodeAt = 0;
  /** reverse-geocode の連続発火を抑止するフラグ (in-flight 中は新規呼び出しをスキップ) */
  private geocodeInFlight = false;

  /** updateInfoPanel のデバウンス用 timer。連続 location/spoofing/context 更新で DOM 全置換が連発しちらつく対策 */
  private updatePanelTimer: number | null = null;

  constructor() {
    this.el = document.createElement('div');
    this.el.className = 'screen';
  }

  render(): HTMLElement {
    // タブ復帰時など 2 回目以降は既存 DOM (= 既存 MapView, 既存 SDK callbacks) を返す。
    // これにより:
    //  - OpenLayers Map インスタンスが破棄されない (再描画コストなし)
    //  - SDK monitoring が止まらない (callbacks 再 set もなし)
    //  - 直近の location / 環境推定 / ジオフェンス候補が即座に表示される
    if (this.built) return this.el;
    this.built = true;
    this.el.innerHTML = '';

    // Header
    const header = document.createElement('div');
    header.className = 'sh';
    header.innerHTML = '<h1>現在地</h1>';
    this.el.appendChild(header);

    // Map area
    const mapArea = document.createElement('div');
    mapArea.className = 'ma';
    this.el.appendChild(mapArea);

    // Map target
    const mapTarget = document.createElement('div');
    mapTarget.style.width = '100%';
    mapTarget.style.height = '100%';
    mapArea.appendChild(mapTarget);

    // Map controls
    const controls = document.createElement('div');
    controls.className = 'mc';
    controls.innerHTML = `
      <button class="mb" id="map-zoom-in">${ICONS.zoomIn}</button>
      <button class="mb" id="map-zoom-out">${ICONS.zoomOut}</button>
      <button class="mb" id="map-refresh-geofences" title="ジオフェンスを更新">${ICONS.refresh}</button>
    `;
    mapArea.appendChild(controls);

    // My location button
    const myLocBtn = document.createElement('button');
    myLocBtn.className = 'mb ml';
    myLocBtn.innerHTML = ICONS.myLocation;
    mapArea.appendChild(myLocBtn);

    // Init OpenLayers
    this.mapView = new MapViewComponent({
      target: mapTarget,
      center: this.currentLocation
        ? [this.currentLocation.lng, this.currentLocation.lat]
        : [139.7638, 35.6862],
      zoom: 16,
    });

    // Spoofing alert
    if (this.alertVisible && this.spoofingStatus) {
      const alertEl = renderSpoofingAlert(this.spoofingStatus, () => {
        this.alertVisible = false;
        alertEl?.remove();
      });
      if (alertEl) mapArea.appendChild(alertEl);
    }

    // Info panel
    const infoPanel = renderLocationInfoPanel(
      this.currentLocation,
      this.address,
      this.spoofingStatus,
      this.currentGeofenceNames,
      this.addressConfidence,
      this.geofenceCandidates,
      this.environment,
      this.environmentAccuracy,
      this.spoofingDetailExpanded,
      (expanded) => {
        this.spoofingDetailExpanded = expanded;
      },
    );
    this.el.appendChild(infoPanel);

    // Wire up controls
    controls.querySelector('#map-zoom-in')!.addEventListener('click', () => this.mapView?.zoomIn());
    controls
      .querySelector('#map-zoom-out')!
      .addEventListener('click', () => this.mapView?.zoomOut());
    const refreshBtn = controls.querySelector('#map-refresh-geofences') as HTMLButtonElement;
    refreshBtn.addEventListener('click', () => this.handleRefreshGeofences(refreshBtn));
    myLocBtn.addEventListener('click', () => {
      if (this.currentLocation) {
        this.mapView?.centerOn(this.currentLocation.lng, this.currentLocation.lat);
      }
      sdkBridge.requestLocation();
    });

    // Apply current state if available
    if (this.currentLocation) {
      this.mapView.setCurrentLocation(this.currentLocation.lng, this.currentLocation.lat);
    }
    if (this.geofences.length > 0) {
      this.mapView.setGeofences(this.geofences);
    }

    // SDK コールバック設定
    this.startBridge();

    return this.el;
  }

  private startBridge(): void {
    const platform = sdkBridge.getPlatform();
    const isNonAndroid = platform !== 'android';

    sdkBridge.setCallbacks({
      onLocationUpdate: (loc) => {
        const isFirstLocation = this.currentLocation === null;
        this.currentLocation = loc;
        this.mapView?.setCurrentLocation(loc.lng, loc.lat);

        // 初回位置取得時は地図をセンタリング
        if (isFirstLocation) {
          this.mapView?.centerOn(loc.lng, loc.lat);
        }

        // iOS/browser fallback: SDK が onLocationContext を発火しないため WebView 側で
        //   - Ray Casting で in/out 判定 → currentGeofenceNames / geofenceCandidates 更新
        //   - サーバ /reverse-geocode で住所取得 (距離 30m / 30s throttle)
        // Android は SDK 経由で onLocationContext が来るため一切実行しない。
        if (isNonAndroid) {
          this.updateGeofenceMembership(loc);
          void this.maybeReverseGeocode(loc);
        }

        this.updateInfoPanel();
      },
      onSpoofingStatus: (status) => {
        this.spoofingStatus = status;
        // iOS/browser: SDK が onLocationContext を発火しないため、
        // onSpoofingStatus payload に同梱される environment / environment_accuracy を拾う。
        // Android では onLocationContext 経由で更新されるため重複しても無害。
        if (typeof status.environment === 'string' && status.environment) {
          this.environment = status.environment;
        }
        if (typeof status.environment_accuracy === 'string' && status.environment_accuracy) {
          this.environmentAccuracy = status.environment_accuracy;
        }
        this.updateInfoPanel();
      },
      onGeofenceEvent: (event) => {
        console.log('[MapPage] Geofence event:', event);
      },
      onLocationContext: (ctx) => {
        // 住所更新
        if (ctx.address) {
          this.address = ctx.address;
        }
        // SDK 側で probability 計算が未実装の間は 0 が来るため、その場合は WebView fallback の値を維持
        if (typeof ctx.address_confidence === 'number' && ctx.address_confidence > 0) {
          this.addressConfidence = ctx.address_confidence;
        }
        // 在圏ジオフェンス更新
        this.currentGeofenceNames = ctx.current_geofences.map((g) => g.name);
        // ジオフェンス確率候補 (Phase 4: in_schedule が false なら "(時間外)" 表示)
        this.geofenceCandidates = (ctx.geofence_candidates || []).map((g) => ({
          name: g.name,
          probability: g.probability,
          inSchedule: g.in_schedule !== false,
        }));
        // Phase 2-b: 環境推定 (UI 診断表示用)
        if (typeof ctx.environment === 'string') {
          this.environment = ctx.environment;
        }
        if (typeof ctx.environment_accuracy === 'string') {
          this.environmentAccuracy = ctx.environment_accuracy;
        }
        // 全ジオフェンスポリゴンをマップに描画（ジオフェンスごとに色分け）
        const GF_COLORS = [
          '#1D4ED8',
          '#059669',
          '#D97706',
          '#7C3AED',
          '#DC2626',
          '#0891B2',
          '#BE185D',
          '#4338CA',
        ];
        const allGf = (ctx.all_geofences || [])
          .filter((g) => g.coordinates && g.coordinates.length >= 3)
          .map(
            (g, i): Geofence => ({
              id: g.id,
              name: g.name,
              description: null,
              category: g.category,
              coordinates: g.coordinates!,
              color: GF_COLORS[i % GF_COLORS.length],
              inSchedule: g.in_schedule !== false,
            }),
          );
        if (allGf.length > 0) {
          this.geofences = allGf;
          this.mapView?.setGeofences(allGf);
        }
        this.updateInfoPanel();
      },
    });
    sdkBridge.startMonitoring();
    sdkBridge.requestLocation();

    // iOS は SDK が onLocationContext を未発火のため初回ジオフェンスを API 直叩きで取得 (Android は onLocationContext で push されるため重複描画回避にスキップ)
    if (isNonAndroid) {
      this.loadInitialGeofences();
    }
  }

  /**
   * iOS/browser fallback: 現在地と既知のジオフェンス polygon を Ray Casting で判定し、
   * `currentGeofenceNames` と `geofenceCandidates` を更新する。
   *
   * 簡易判定 (in なら確率 1.0、out なら候補に含めない) のみ。確率計算 (2D ガウス × グリッド積分) は
   * Android SDK の実装と一致させない (Android では SDK の onLocationContext が呼ばれるため本処理不要)。
   */
  private updateGeofenceMembership(loc: LocationData): void {
    const insideNames: string[] = [];
    const candidates: { name: string; probability: number; inSchedule: boolean }[] = [];
    for (const gf of this.geofences) {
      if (!gf.coordinates || gf.coordinates.length < 3) continue;
      // 時間外 polygon は判定対象から除外 (UI で "(時間外)" 表示済み、入域扱いにしない)
      if (gf.inSchedule === false) continue;
      if (pointInPolygon(loc.lng, loc.lat, gf.coordinates)) {
        insideNames.push(gf.name);
        candidates.push({ name: gf.name, probability: 1.0, inSchedule: true });
      }
    }
    this.currentGeofenceNames = insideNames;
    this.geofenceCandidates = candidates;
  }

  /**
   * iOS/browser fallback: サーバ /reverse-geocode を叩いて住所を更新する。
   *
   * 負荷軽減のため、直前呼び出しから 30m 未満かつ 30s 未満の場合はスキップ。
   * 同時実行は in-flight フラグで 1 件に制限する。
   * レスポンスの最初の feature を 100% として 1 候補のみ表示する簡易実装。
   */
  private async maybeReverseGeocode(loc: LocationData): Promise<void> {
    if (this.geocodeInFlight) return;
    const now = Date.now();
    const ageMs = now - this.lastGeocodeAt;
    const moved =
      this.lastGeocodeLat !== null && this.lastGeocodeLng !== null
        ? distanceMeters(this.lastGeocodeLat, this.lastGeocodeLng, loc.lat, loc.lng)
        : Number.POSITIVE_INFINITY;
    // 30m 未満かつ 30s 未満なら直前結果を維持 (初回 = moved=Infinity で必ず叩く)
    if (moved < 30 && ageMs < 30_000) return;

    this.geocodeInFlight = true;
    try {
      const res = await eventService.reverseGeocode(loc.lat, loc.lng);
      this.lastGeocodeLat = loc.lat;
      this.lastGeocodeLng = loc.lng;
      this.lastGeocodeAt = Date.now();
      // 中心 + 隣接 9 メッシュの全候補から、現在地に最も近い center を持つ feature を選ぶ。
      // 最初の feature は KEY_CODE 順なので現在地と無関係になるためバグの原因だった。
      let nearest: (typeof res.features)[number] | null = null;
      let minDist = Number.POSITIVE_INFINITY;
      for (const f of res.features ?? []) {
        const c = f.properties.center;
        if (!c || typeof c.lat !== 'number' || typeof c.lng !== 'number') continue;
        const d = distanceMeters(loc.lat, loc.lng, c.lat, c.lng);
        if (d < minDist) {
          minDist = d;
          nearest = f;
        }
      }
      if (nearest) {
        const p = nearest.properties;
        const parts = [p.PREF_NAME, p.CITY_NAME, p.S_NAME].filter(
          (s) => typeof s === 'string' && s.length > 0,
        );
        const addr = parts.join('');
        if (addr) {
          this.address = addr;
          this.addressConfidence = 1.0;
        }
      }
      this.updateInfoPanel();
    } catch (e) {
      console.warn('[MapPage] reverseGeocode failed', e);
    } finally {
      this.geocodeInFlight = false;
    }
  }

  /** iOS/browser 用: SDK 経路でジオフェンスが届かないため API から初回ロード */
  private async loadInitialGeofences(): Promise<void> {
    try {
      const res = await eventService.listGeofences();
      if (res.geofences.length > 0 && this.geofences.length === 0) {
        this.geofences = res.geofences;
        this.mapView?.setGeofences(res.geofences);
      }
    } catch (e) {
      console.warn('[MapPage] initial listGeofences failed', e);
    }
  }

  /**
   * ジオフェンス定義をサーバから即時再取得する。
   *
   * SDK は通常 5 分間隔で自動再取得するが、admin が新しい polygon を追加した直後に
   * 即時反映したい場面で本ボタンを押下する。
   *
   * 動作:
   * - Android: SDK 内部の定義を更新 → `onLocationContext` で `all_geofences` が再 push される
   *   (色割り当ては SDK→onLocationContext の経路で適用)
   * - iOS / browser: SDK 経路は no-op のため、`/geofences` API を直接叩いて MapView を更新
   */
  private async handleRefreshGeofences(btn: HTMLButtonElement): Promise<void> {
    if (btn.disabled) return;
    btn.disabled = true;
    btn.classList.add('loading');
    try {
      sdkBridge.refreshGeofences();
      const res = await eventService.listGeofences();
      if (res.geofences.length > 0) {
        this.geofences = res.geofences;
        this.mapView?.setGeofences(res.geofences);
      }
    } catch (e) {
      console.error('[MapPage] refreshGeofences failed', e);
    } finally {
      btn.classList.remove('loading');
      btn.disabled = false;
    }
  }

  private updateInfoPanel(): void {
    // 連続更新で DOM 全置換が走るとちらつくため 120ms デバウンス
    if (this.updatePanelTimer !== null) {
      clearTimeout(this.updatePanelTimer);
    }
    this.updatePanelTimer = window.setTimeout(() => {
      this.updatePanelTimer = null;
      const oldPanel = this.el.querySelector('.ip');
      if (oldPanel) {
        const newPanel = renderLocationInfoPanel(
          this.currentLocation,
          this.address,
          this.spoofingStatus,
          this.currentGeofenceNames,
          this.addressConfidence,
          this.geofenceCandidates,
          this.environment,
          this.environmentAccuracy,
          this.spoofingDetailExpanded,
          (expanded) => {
            this.spoofingDetailExpanded = expanded;
          },
        );
        oldPanel.replaceWith(newPanel);
      }
    }, 120);
  }

  destroy(): void {
    sdkBridge.stopMonitoring();
    this.mapView?.destroy();
    this.mapView = null;
  }
}
